import { EventListItem } from '@unistocks/components/event-list-item/event-list-item';
import { Group } from '@unistocks/components/group/group';
import { CardProps } from '@unistocks/components/layout/card';
import { Page } from '@unistocks/components/page/page';
import { RenderTab, Tab, Tabs } from '@unistocks/components/tabs/tabs';
import { H4 } from '@unistocks/components/text/text';
import { useAssetImage } from '@unistocks/features/assets/hooks/use-asset-image';
import { AssetDto, AssetEventDto, EventType } from '@unistocks/proxy/axios-client';
import { useAssetsGetEventsQuery } from '@unistocks/proxy/axios-client/AssetsQuery';
import { formatDate, t } from '@unistocks/shared/localization/index.ts';
import { groupBy, sortByKey } from '@unistocks/shared/utils/util';
import { Fragment } from 'react';
import { Separator } from 'tamagui';

type AssetEventItemProps = {
  event: AssetEventDto;
  asset: AssetDto;
};

const AssetEventItem: React.FC<AssetEventItemProps> = ({ event, asset }) => {
  const image = useAssetImage(asset, { preferAssetImage: true });

  return (
    <EventListItem
      type={event.type}
      subType={event.subType}
      title={asset.name}
      image={image}
      value={
        event.value != null && event.currency != null
          ? { value: event.value, currency: event.currency }
          : null
      }
    />
  );
};

AssetEventItem.displayName = 'AssetEventItem';

type AssetEventGroupListProps = {
  events: AssetEventDto[];
  asset: AssetDto;
};

const AssetEventGroupList: React.FC<AssetEventGroupListProps> = ({ events, asset }) => {
  const groupedEvents = Array.from(groupBy(events, 'date')).sort(sortByKey('0'));

  return groupedEvents.map(([date, dateEvents]) => (
    <Fragment key={date}>
      <H4 color={'$headingFont'} userSelect="none">
        {formatDate(date, { month: 'long', day: 'numeric' })}
      </H4>
      <Group separator={<Separator />}>
        {dateEvents.map((event, index) => (
          <Group.Item key={index}>
            <AssetEventItem event={event} asset={asset} />
          </Group.Item>
        ))}
      </Group>
      <Separator my={'$1'} />
    </Fragment>
  ));
};

AssetEventGroupList.displayName = 'AssetEventGroupList';

export type AssetEventsProps = {
  asset: AssetDto;
} & CardProps;

export const AssetEvents: React.FC<AssetEventsProps> = ({ asset, ...props }) => {
  const { data, isSuccess } = useAssetsGetEventsQuery(asset.id);

  const dividends = (data?.items ?? []).filter(event => event.type === EventType.Dividend);
  const reports = (data?.items ?? []).filter(event => event.type === EventType.Earnings);

  const tabs: Tab[] = [];
  if (dividends.length > 0) tabs.push({ label: t('::Asset:Dividends'), value: 'dividends' });
  if (reports.length > 0) tabs.push({ label: t('::Asset:Reports'), value: 'reports' });

  const renderTab: RenderTab = tab => {
    const events = tab === 'dividends' ? dividends : reports;

    return <AssetEventGroupList events={events} asset={asset} />;
  };

  if (!isSuccess || tabs.length === 0) return null;

  return (
    <Page.Content testID="asset-events-card" {...props}>
      <Page.Content.Header p={0}>
        <Page.Content.Heading>{t('::Asset:Events')}</Page.Content.Heading>
      </Page.Content.Header>
      <Tabs tabs={tabs} renderTab={renderTab} />
    </Page.Content>
  );
};

AssetEvents.displayName = 'AssetEvents';
